import React from 'react';
import './StreamingPipelineStatus.css';

/** In-bubble copy: short progressive labels (server sends stable ``id``). */
const BUBBLE_ING = {
  retrieval_query_classic: 'Building search query…',
  query_rewrite: 'Rewriting query…',
  retrieval: 'Searching documents…',
  context_assembly: 'Preparing context…',
  llm_request: 'Generating answer…',
  thinking: 'Checking extras…',
};

function isLlmStep(step) {
  const id = String(step?.id || '');
  return id === 'llm_request' || id.includes('llm');
}

/** Latest agent mid-turn narration from pipeline trace (user-friendly progress text). */
export function latestAgentNarration(trace) {
  const steps = trace?.steps;
  if (!Array.isArray(steps)) return '';
  for (let i = steps.length - 1; i >= 0; i--) {
    const s = steps[i];
    if (String(s?.id || '').startsWith('agent_narration')) {
      const text = s?.detail?.text;
      if (text) return String(text).trim();
    }
  }
  return '';
}

function stepStatusLabel(step) {
  const id = String(step?.id || '');
  if (id.startsWith('agent_narration')) {
    return latestAgentNarration({ steps: [step] }) || 'Working…';
  }
  if (id === 'agent_tool' && step?.status === 'running') {
    const name = step?.detail?.tool_name || step?.title || '';
    const bare = String(name).split('__').pop() || 'tool';
    return `Running ${bare.replace(/_/g, ' ')}…`;
  }
  const fallback = (id && BUBBLE_ING[id]) || String(step?.title || step?.id || '').trim();
  return fallback || 'Working…';
}

/**
 * In-bubble status while streaming: prefer agent narration text over generic labels.
 * Hidden while assistant tokens are visible in the bubble.
 */
export default function StreamingPipelineStatus({ trace, hasAnswerText }) {
  if (hasAnswerText) return null;

  const narration = latestAgentNarration(trace);
  if (narration) {
    return (
      <div className="streaming-pipeline-status streaming-pipeline-status--narration" aria-live="polite">
        <span className="streaming-pipeline-status__text">
          <span className="streaming-pipeline-status__label">{narration}</span>
        </span>
      </div>
    );
  }

  const steps = trace?.steps;
  const hasSteps = Array.isArray(steps) && steps.length > 0;
  let labelPart = '';

  if (hasSteps) {
    const runningIdx = steps.findIndex((s) => s.status === 'running');
    if (runningIdx >= 0) {
      labelPart = stepStatusLabel(steps[runningIdx]);
    } else {
      let i = steps.length - 1;
      while (i >= 0) {
        const s = steps[i];
        if (isLlmStep(s) || s.id === 'thinking') {
          i -= 1;
          continue;
        }
        labelPart = stepStatusLabel(s);
        break;
      }
    }
  }

  if (!labelPart) return null;

  return (
    <div className="streaming-pipeline-status" aria-live="polite">
      <span className="streaming-pipeline-status__text">
        <span className="streaming-pipeline-status__label">{labelPart}</span>
      </span>
      <span className="streaming-pipeline-status__shine" aria-hidden />
    </div>
  );
}
